import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { DeliveryLog, ProjectActivityChart, ProjectFeedList, ReliabilityPanel } from "../components/index.js";
import { useApiClient } from "../api/ApiContext.jsx";
import { buildPollingSummary } from "../api/mappers.js";
import { DEFAULT_SORT, sortParams } from "../ui/feedSorting.js";
import { useApi } from "../hooks/useApi.js";
import { useDebounced } from "../hooks/useDebounced.js";
import { AsyncBoundary, ErrorView, InlineMessage, PageLoading, errorText } from "../ui/Feedback.jsx";

export default function ProjectScreen() {
  const { projectId } = useParams();
  const [tab, setTab] = useState("feeds");
  // Arriving from a provider row means the list is already narrowed. It is read
  // once, as the starting state, rather than bound to the URL: the customer can
  // then clear it in the list without the address bar fighting them back.
  const [searchParams] = useSearchParams();
  const [providerFilter, setProviderFilter] = useState(searchParams.get("provider") ?? "");

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 20, alignItems: "center" }}>
        <TabButton active={tab === "feeds"} onClick={() => setTab("feeds")}>
          Feeds
        </TabButton>
        <TabButton active={tab === "deliveries"} onClick={() => setTab("deliveries")}>
          Delivery log
        </TabButton>
        <TabButton active={tab === "reliability"} onClick={() => setTab("reliability")}>
          Reliability
        </TabButton>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <Link className="ical-btn" to={`/projects/${projectId}/settings`} style={{ textDecoration: "none" }}>
            Settings
          </Link>
          <Link className="ical-btn" to={`/projects/${projectId}/edit`} style={{ textDecoration: "none" }}>
            Edit
          </Link>
        </div>
      </div>

      {tab === "feeds" ? (
        <FeedsTab projectId={projectId} providerFilter={providerFilter} onProviderFilter={setProviderFilter} />
      ) : null}
      {tab === "deliveries" ? <DeliveriesTab projectId={projectId} /> : null}
      {tab === "reliability" ? (
        <>
          {/* The screen answers "how is it going"; acting on it is a setting. */}
          <p style={{ margin: "0 0 10px", fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>
            To be notified when a calendar or a provider crosses a failure rate, set the thresholds
            in <Link to={`/projects/${projectId}/settings`}>project settings</Link>.
          </p>
          {/* The same component the admin screen uses, scoped to this project: the
              two views are one measurement, and a customer asking "is it me or the
              provider" is asking what the administrator's screen answers globally. */}
          <ReliabilityPanel
            scope="project"
            projectId={projectId}
            onOpenFeed={(feedId) => navigate(`/feeds/${feedId}`)}
          />
        </>
      ) : null}
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      className={`ical-btn${active ? " ical-btn-primary" : ""}`}
      onClick={onClick}
      style={{ padding: "6px 14px", fontSize: "var(--text-sm)" }}
    >
      {children}
    </button>
  );
}

/**
 * ProjectFeedList needs two contract reads: `/projects/{id}` supplies the
 * delivery mode (the channels response's `project.mode` is the *polling* mode)
 * and `/projects/{id}/channels` supplies the rows.
 *
 * The list is a page, not a growing window: the ordering, the page size and the
 * page are part of the query, so a customer with a few thousand calendars sees
 * fifty rows and can jump to the page they want instead of pulling everything into
 * the browser and sorting it there. Anything that changes the result set — a new
 * search, a new ordering, a new page size — starts again at page 1, because an
 * offset computed for the previous result set shows a window nobody asked for.
 */
const DEFAULT_PAGE_SIZE = 50;

function FeedsTab({ projectId, providerFilter, onProviderFilter }) {
  const api = useApiClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search, 300);
  const [error, setError] = useState(null); // ApiError | null
  const [note, setNote] = useState(null); // what a deletion actually collected
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sort, setSort] = useState(DEFAULT_SORT);

  // A new search is a new result set, so the page the customer was on no longer
  // means anything — staying on page 4 of a one-page result would show nothing.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, providerFilter, projectId]);

  const detailQuery = useApi(() => api.projects.get(projectId), [api, projectId]);
  const channelsQuery = useApi(
    () => api.projects.channels(projectId, {
      q: debouncedSearch,
      provider: providerFilter,
      limit: pageSize,
      offset: (page - 1) * pageSize,
      ...sortParams(sort),
    }),
    [api, projectId, debouncedSearch, providerFilter, pageSize, page, sort],
  );

  // Removing rows from the last page can leave the current page past the end, and
  // an empty list with "Page 4 of 3" under it reads as a broken screen.
  const pageCount = Math.max(1, Math.ceil((channelsQuery.data?.total ?? 0) / pageSize));
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  function handleSortChange(next) {
    setSort(next);
    setPage(1);
  }

  function handlePageSizeChange(size) {
    setPageSize(size);
    setPage(1);
  }

  // The activity chart is its own read, and a failure of it must not take the feed
  // list down with it: the list is what the screen is for.
  const activityQuery = useApi(() => api.projects.activity(projectId, { hours: 24 }), [api, projectId]);

  function reloadAll() {
    detailQuery.reload();
    channelsQuery.reload();
    activityQuery.reload();
  }

  async function handleAddFeed(url, externalId) {
    setError(null);
    try {
      await api.projects.addChannel(projectId, { url, externalId });
      channelsQuery.reload();
    } catch (err) {
      setError(err);
    }
  }

  /**
   * Removing a channel reports what it collected, and the report is shown
   * rather than swallowed: whether the calendar behind it was actually deleted
   * depends on other projects the customer cannot see from here.
   *
   * It answers whether the deletion happened, so the row keeps its confirmation
   * open after a failure: a row that went back to normal would read as a
   * deletion that succeeded, and the retry would be two clicks instead of one.
   */
  async function handleRemoveFeed(channel) {
    setError(null);
    setNote(null);
    try {
      const { cleanup } = await api.projects.removeChannel(projectId, channel.externalId);
      setNote(
        cleanup.feedRemoved
          ? `${channel.externalId} removed. The calendar was not used by any other project, so its record, cached events and request log were removed too.`
          : `${channel.externalId} removed from this project. The calendar is still polled for another project, so its data was kept.`,
      );
      channelsQuery.reload();
      detailQuery.reload();
      return true;
    } catch (err) {
      setError(err);
      return false;
    }
  }

  if (detailQuery.loading || channelsQuery.loading) return <PageLoading />;

  const failed = detailQuery.error || channelsQuery.error;
  if (failed) {
    return (
      <div>
        <InlineMessage tone="danger">{errorText(failed)}</InlineMessage>
        <ErrorView error={failed} onRetry={reloadAll} onBack={() => navigate("/")} />
      </div>
    );
  }

  const detail = detailQuery.data;
  const channelPage = channelsQuery.data;
  const project = {
    name: channelPage.project.name ?? detail.name,
    feedCount: channelPage.project.feedCount || detail.feedCount || channelPage.total,
    // Component speaks `content` / `notify_only`; the API said `content_mode`.
    mode: detail.mode,
    pollingSummary: channelPage.project.pollingSummary ?? buildPollingSummary(detail),
  };

  return (
    <div>
      {/* First thing on the screen, above the tabs: a customer whose deliveries are
          suspended is looking at a quiet log and needs to know why before they read
          anything else. `status` is the lifecycle state, which the panel used to
          omit — the mode label said "Active" while nothing was being sent. */}
      {detail.status === "failing" ? (
        <InlineMessage tone="danger">
          Deliveries to your endpoint are suspended: it has been failing for long enough
          that we stopped sending. Nothing is lost — change notifications are held and go
          out in order once your endpoint answers again, and we keep checking it. Fix the
          callback URL in{" "}
          <Link to={`/projects/${projectId}/settings`}>project settings</Link> and press Test to
          resume now.
        </InlineMessage>
      ) : null}
      {detail.status === "suspended" ? (
        <InlineMessage tone="warning">
          Deliveries are switched off for this project, so nothing is being sent to your
          endpoint. Change notifications raised meanwhile are held and sent in order once it
          is active again.
        </InlineMessage>
      ) : null}
      {note ? <InlineMessage tone="info">{note}</InlineMessage> : null}
      {error ? (
        <InlineMessage tone={error.code === "conflict" ? "warning" : "danger"}>
          {error.code === "conflict"
            ? "That feed is already subscribed in this project."
            : errorText(error, "Could not add the feed.")}
          {error.code === "quota_exceeded" ? (
            <>
              {" "}
              <button
                type="button"
                onClick={() => navigate("/upgrade")}
                style={{ color: "var(--text-accent)", background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: "inherit" }}
              >
                See plan options
              </button>
              .
            </>
          ) : null}
        </InlineMessage>
      ) : null}

      {activityQuery.loading ? (
        <div className="ical-card" style={{ marginBottom: 16 }}>
          <PageLoading />
        </div>
      ) : activityQuery.error ? (
        <div style={{ marginBottom: 16 }}>
          <InlineMessage tone="warning">
            The activity chart could not be loaded ({errorText(activityQuery.error, "request failed")}).
            The feed list below is unaffected.
          </InlineMessage>
        </div>
      ) : (
        <div style={{ marginBottom: 16 }}>
          <ProjectActivityChart data={activityQuery.data} />
        </div>
      )}

      <ProjectFeedList
        project={project}
        feeds={channelPage.channels}
        totalFeedCount={channelPage.total}
        page={page}
        pageSize={pageSize}
        sort={sort}
        onAddFeed={handleAddFeed}
        onRemoveFeed={handleRemoveFeed}
        onOpenFeed={(feedId) => navigate(`/feeds/${feedId}`)}
        providerFilter={providerFilter}
        providerOptions={detail.providers ?? []}
        onProviderChange={onProviderFilter}
        onClearProvider={() => onProviderFilter("")}
        onSearch={setSearch}
        onSortChange={handleSortChange}
        onPageChange={setPage}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  );
}

function DeliveriesTab({ projectId }) {
  const api = useApiClient();
  const navigate = useNavigate();
  const [status, setStatus] = useState("all");
  const [note, setNote] = useState(null);

  const query = useApi(
    () => api.projects.deliveries(projectId, { status, limit: 50 }),
    [api, projectId, status],
  );

  return (
    <div>
      {note ? <InlineMessage tone="info">{note}</InlineMessage> : null}
      <AsyncBoundary query={query} onBack={() => navigate("/")}>
        {(data) => (
          <DeliveryLog
            project={data.project}
            deliveries={data.deliveries}
            activeRetry={data.activeRetry ?? undefined}
            payloadPreview={data.payloadPreview ?? undefined}
            onFilterChange={setStatus}
            // The contract has no per-delivery endpoint (panel-decisions 3.5),
            // so say that instead of pretending the click did something.
            onOpenDelivery={() =>
              setNote(
                "Per-delivery detail isn't exposed by the panel API yet — only the aggregate row and the active retry timeline.",
              )
            }
          />
        )}
      </AsyncBoundary>
    </div>
  );
}
